import axios from "axios";
import apiURL from "../apiURL";
import { checkSessionExpiration } from "../checkSessionExpiration";
import { handleAxiosError } from "../axiosErrorHandler";

const baseURL = apiURL();

export interface IExpenseRequestRow {
  expenseRequestID: number;
  choice: string;
  department?: string;
  requestBy: string;
  requestDate: Date;
  requestTime?: string;
  edrNo: string;
  purpose: string;
  dateLastPurchased: Date;
  requestAmount: number;
  requestAmountGST: number;
  preparedBy: string;
  passedBy?: string;
  passedDate?: Date;
  approvedBy?: string;
  approvedDate?: Date;
  receivedBy?: string;
  receivedDate?: Date;
  employeeID: number;
  chequeNo: string;
  bank: string;
  extraInfo: string;
  requestID: number;
  requestType: string;
  requestAssigned: string;
  requestAssignedID: number;
  bwipVoucher: string;
  exRequestDocRef: string;
  requestStatus: string;
  rejectionNote: string;
  rejected: boolean;
  closed: boolean;
  receiverSignature: string;
  requestStatusOrder: number;
  queried: boolean;
  requireReceipt: boolean;
}

export interface IExpenseRequestData {
  data: IExpenseRequestRow[];
}
interface IGetMissing {
  token: string;
  choice?: string | undefined;
  /** Uses server-resolved workflow relevance; record view-all scope does not grant notifications. */
  notificationMode?: boolean;
  activeOnly?: boolean;
  /** Server-enforced visibility scope: "mine" (own requests only) or "all" (caller's full
   * permitted role scope - literally everything only for CanViewAllExpenses). Defaults to "mine"
   * server-side if omitted. */
  scope?: "mine" | "all";
}
const GetExpenseRequests = async (props: IGetMissing) => {
  const { token, choice, notificationMode, activeOnly, scope } = props;

  // Check session expiration before proceeding
  if (!checkSessionExpiration()) {
    return;
  }

  try {
    const url = new URL(`${baseURL}ExpenseRequest/getExpenseRequests`);
    if (choice) {
      url.searchParams.append("choice", choice);
    }
    if (notificationMode) {
      url.searchParams.append("notificationMode", "true");
    }
    if (activeOnly) {
      url.searchParams.append("activeOnly", "true");
    }
    if (scope) {
      url.searchParams.append("scope", scope);
    }

    const data = await axios({
      method: "get",
      responseType: "json",
      url: url.toString(),
      headers: { Authorization: `Bearer ${token}` },
    });

    return data as IExpenseRequestData;
  } catch (error) {
    // Call the handleAxiosError function with the error and redirect URL
    handleAxiosError(error, "/login"); // Adjust the URL as needed
    return undefined;
  }
};

/**
 * Secure single-expense lookup used for notification deep-linking, for when the referenced
 * request is not already present in a currently-loaded list. Applies the exact same server-side
 * access rules as every other expense endpoint.
 *
 * Returns:
 * - the row, if the caller is authorized to see it
 * - `null` if the request does not exist or the caller is no longer authorized to see it (safe,
 *   IDOR-proof: the caller cannot tell these two cases apart)
 * - `undefined` on an unrelated/unexpected error (session expiry etc., already handled)
 */
export const getExpenseRequestById = async (
  token: string,
  requestID: number,
): Promise<IExpenseRequestRow | null | undefined> => {
  if (!checkSessionExpiration()) {
    return undefined;
  }

  try {
    const response = await axios({
      method: "get",
      responseType: "json",
      url: `${baseURL}ExpenseRequest/getExpenseRequestById`,
      params: { requestID },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data as IExpenseRequestRow;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    handleAxiosError(error, "/login");
    return undefined;
  }
};

export default GetExpenseRequests;
